// src/pages/Customer/CartPage/CartSheet.tsx
import React, { useMemo, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ShoppingBag, Plus, Minus, Trash2, ArrowRight } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useTheme } from "../../../../context/ThemeContext";
import { useCart } from "../../../../context/CartContext/CartContext";

/* ================= Theme surfaces ================= */
const hexToRgb = (hex: string) => {
  const h = (hex || "#0f1115").replace("#", "");
  const full = h.length === 3 ? h.split("").map(c => c + c).join("") : h;
  const n = parseInt(full, 16);
  return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 };
};
const luminance = ({ r, g, b }: { r: number; g: number; b: number }) => {
  const f = (v: number) => { v /= 255; return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4); };
  return 0.2126 * f(r) + 0.7152 * f(g) + 0.0722 * f(b);
};
const useSurfaces = (theme?: any) => {
  const bg = theme?.backgroundColor || "#0f1115";
  const isDark = luminance(hexToRgb(bg)) < 0.5;
  return {
    bg,
    surface1: isDark ? "rgba(255,255,255,.04)" : "rgba(0,0,0,.04)",
    surface2: isDark ? "rgba(255,255,255,.07)" : "rgba(0,0,0,.06)",
    divider:  isDark ? "rgba(255,255,255,.10)" : "rgba(0,0,0,.12)",
    textPrimary: theme?.textPrimary ?? (isDark ? "#fff" : "#111827"),
    textSecondary: theme?.textSecondary ?? (isDark ? "rgba(255,255,255,.7)" : "#6b7280"),
    accent: theme?.accent ?? "#22c55e",
  };
};
const panel = (S: ReturnType<typeof useSurfaces>): React.CSSProperties => ({
  backgroundColor: S.surface1, borderColor: S.divider, borderWidth: 1
});
const soft = (S: ReturnType<typeof useSurfaces>): React.CSSProperties => ({
  backgroundColor: S.surface2, borderColor: S.divider, borderWidth: 1
});

/* ================= Utils ================= */
const formatCurrency = (v: number, ccy = "TRY") =>
  new Intl.NumberFormat("tr-TR", { style: "currency", currency: ccy }).format(v || 0);

const CartSheet: React.FC = () => {
  const nav = useNavigate();
  const { theme } = useTheme();
  const S = useSurfaces(theme);

  const {
    isOpen,
    close,
    items,
    totals,
    changeQty,
    removeItem,
    currency = "TRY",
  } = useCart();

  const grand = useMemo(() => totals?.toplamBrut ?? 0, [totals]);
  const empty = !items || items.length === 0;

  /* ==== BODY SCROLL LOCK ==== */
  useEffect(() => {
    if (!isOpen) return;

    const { body, documentElement } = document;
    const prevOverflow = body.style.overflow;
    const prevPaddingRight = body.style.paddingRight;

    // scrollbar genişliği (layout zıplamasını önlemek için)
    const scrollbarWidth = window.innerWidth - documentElement.clientWidth;

    body.style.overflow = "hidden";
    if (scrollbarWidth > 0) {
      body.style.paddingRight = `${scrollbarWidth}px`;
    }

    // mobil overscroll’u sınırlamak için
    const prevOverscroll = documentElement.style.overscrollBehavior;
    documentElement.style.overscrollBehavior = "contain";

    return () => {
      body.style.overflow = prevOverflow;
      body.style.paddingRight = prevPaddingRight;
      documentElement.style.overscrollBehavior = prevOverscroll;
    };
  }, [isOpen]);

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* overlay */}
          <motion.div
            className="fixed inset-0 z-[100]"
            style={{  }}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={close}
          />

          {/* drawer */}
          <motion.aside
            role="dialog"
            aria-modal="true"
            aria-label="Sepet"
            className="fixed right-0 top-0 h-full w-full sm:w-[420px] z-[110] flex flex-col"
            style={{ backgroundColor: S.bg, borderLeft: `1px solid ${S.divider}` }}
            initial={{ x: 40, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: 40, opacity: 0 }}
            transition={{ type: "tween", duration: 0.2 }}
          >
            {/* header */}
            <div className="h-14 px-4 flex items-center justify-between" style={{ borderBottom: `1px solid ${S.divider}` }}>
              <div className="font-semibold" style={{ color: S.textPrimary }}>
                Sepetim
              </div>
              <button
                aria-label="Kapat"
                onClick={close}
                className="w-9 h-9 inline-flex items-center justify-center rounded-md border"
                style={soft(S)}
              >
                <X size={18} style={{ color: S.textPrimary }} />
              </button>
            </div>

            {/* content */}
            <div className="flex-1 overflow-auto p-3 space-y-2">
              {empty ? (
                <div className="p-6 text-center" style={panel(S)}>
                  <div className="mx-auto w-14 h-14 rounded-full grid place-items-center mb-2" style={{ backgroundColor: S.surface2 }}>
                    <ShoppingBag size={22} style={{ color: S.textSecondary }} />
                  </div>
                  <div className="text-sm" style={{ color: S.textSecondary }}>
                    Sepetiniz boş.
                  </div>
                </div>
              ) : (
                items.map((it: any) => {
                  const lineNet = (it.birimFiyatNet - (it.satirIndirimNet ?? 0)) * it.adet;
                  const lineTax = lineNet * ((it.vergiOraniYuzde ?? 0) / 100);
                  const lineGross = lineNet + lineTax;
                  return (
                    <div key={it.sepetKalemId} className="rounded-lg p-3" style={panel(S)}>
                      <div className="flex gap-3">
                        <div className="w-16 h-16 rounded-md grid place-items-center shrink-0" style={{ backgroundColor: S.surface2 }}>
                          <ShoppingBag size={18} style={{ color: S.textSecondary }} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="font-medium truncate" style={{ color: S.textPrimary }}>
                            {it.urunAdi ?? `Ürün #${it.urunId}`}
                          </div>
                          {it.secenekOzeti && (
                            <div className="text-xs truncate" style={{ color: S.textSecondary }}>{it.secenekOzeti}</div>
                          )}

                          <div className="mt-2 flex items-center justify-between">
                            <div className="inline-flex items-center gap-2">
                              <button
                                onClick={() => changeQty(it.sepetKalemId, Math.max(1, it.adet - 1))}
                                className="w-8 h-8 inline-flex items-center justify-center rounded-md border"
                                style={soft(S)}
                              >
                                <Minus size={16} style={{ color: S.textPrimary }} />
                              </button>
                              <input
                                className="h-8 w-12 text-center rounded-md border"
                                style={soft(S)}
                                type="number"
                                min={1}
                                value={it.adet}
                                onChange={(e) =>
                                  changeQty(it.sepetKalemId, Math.max(1, Number(e.target.value) || 1))
                                }
                              />
                              <button
                                onClick={() => changeQty(it.sepetKalemId, it.adet + 1)}
                                className="w-8 h-8 inline-flex items-center justify-center rounded-md border"
                                style={soft(S)}
                              >
                                <Plus size={16} style={{ color: S.textPrimary }} />
                              </button>
                            </div>

                            <div className="text-right">
                              <div className="font-semibold" style={{ color: S.textPrimary }}>
                                {formatCurrency(lineGross, currency)}
                              </div>
                              <button
                                onClick={() => removeItem(it.sepetKalemId)}
                                className="mt-1 inline-flex items-center gap-1 text-xs"
                                style={{ color: "#b91c1c" }}
                              >
                                <Trash2 size={14} /> Kaldır
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>

            {/* footer */}
            <div className="p-3 space-y-2" style={{ borderTop: `1px solid ${S.divider}` }}>
              <div className="flex items-center justify-between">
                <div className="text-sm" style={{ color: S.textSecondary }}>
                  Genel Toplam
                </div>
                <div className="text-lg font-semibold" style={{ color: S.textPrimary }}>
                  {formatCurrency(grand, currency)}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-2">
                <button
                  onClick={() => {
                    close();
                    nav("/musteri/panel/sepet");
                  }}
                  className="h-11 rounded-md font-medium inline-flex items-center justify-center gap-2 border"
                  style={soft(S)}
                >
                  Sepete Git
                </button>
                <button
                  onClick={() => { close(); nav("/musteri/checkout"); }}
                  className="h-11 rounded-md text-white font-medium inline-flex items-center justify-center gap-2"
                  style={{ background: S.accent }}
                >
                  Ödeme <ArrowRight size={18} />
                </button>
              </div>
            </div>
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
};

export default CartSheet;
